import React, { useEffect, useRef, useState } from 'react'
import Select, {
  components,
  ActionMeta,
  MultiValue,
  Props as SelectProps,
} from 'react-select'
import { useField } from '@unform/core'
import { Icon } from 'assets/index'
import { Checkbox } from './style'
import {
  InputError,
  InputContainer,
  Placeholder,
} from '../FormCommomStyle/FormCommomStyle'
import { selectStyles } from './factory'

// Definição dos tipos para as opções
export interface ColourOption {
  readonly value: string
  readonly label: string
  readonly color?: string
  readonly isFixed?: boolean
  readonly isDisabled?: boolean
}

interface ISelectProps extends SelectProps {
  name: string
  placeholder?: string
  disabled?: boolean
  small?: boolean
  large?: boolean
  width?: number
  hasAsterisk?: boolean
  dataTestId: string
  options: ColourOption[]
  defaultValue?: any
  onChange?: (_ev: any) => void
}

// Componente Option customizado com checkbox
const Option: React.FC<any> = (props) => {
  const { label, isSelected, data, selectProps } = props
  const inputId = `checkbox-${selectProps.name}-${data.value}`

  return (
    <div>
      <components.Option {...props}>
        <Checkbox>
          <label
            className="custom-checkbox-container"
            htmlFor={inputId} // associando corretamente
          >
            <input
              id={inputId} // identificador único
              type="checkbox"
              checked={isSelected}
              onChange={() => null}
              className="original-checkbox"
            />
            <span className="custom-checkbox" />
            <span className="checkbox-label">{label}</span>
          </label>
        </Checkbox>
      </components.Option>
    </div>
  )
}
// Componente Value Container customizado para não mostrar as tags
const ValueContainer: React.FC<any> = ({ children, ...props }) => {
  const selectedOptions = props.getValue() as ColourOption[]

  const labels = selectedOptions.map((opt) => opt.label).join(', ')

  const childrenWithoutValues = React.Children.toArray(children).filter(
    (child) => {
      if (React.isValidElement(child) && child.type === components.MultiValue) {
        return false
      }
      return true
    }
  )

  return (
    <components.ValueContainer {...props}>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
          color: props.isDisabled ? 'rgb(84, 84, 84)' : 'black',
        }}
        title={labels}
      >
        {labels}
      </div>
      {childrenWithoutValues}
    </components.ValueContainer>
  )
}

// Componente principal
const MultiSelect: React.FC<ISelectProps> = (props) => {
  const {
    name,
    placeholder,
    disabled,
    small,
    large,
    width,
    hasAsterisk,
    defaultValue,
    onChange,
    dataTestId,
    options,
  } = props
  const [showError, setShowError] = useState<boolean>(false)
  const { fieldName, registerField, error } = useField(name)
  const selectRef = useRef<any>(null)

  const [selectedOptions, setSelectedOptions] = useState<
    readonly ColourOption[]
  >([])

  const handleChange = (newValue: MultiValue<ColourOption>) => {
    const values = newValue as readonly ColourOption[]
    setSelectedOptions(values)
    if (onChange) onChange(values)
    // if (onChange) onChange(values.map((v) => v.value))
  }

  useEffect(() => {
    if (defaultValue) {
      setSelectedOptions(defaultValue)
    }
  }, [defaultValue])

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectRef.current,
      getValue: (ref: any) => {
        if (!ref?.state?.selectValue) return []
        return ref.state.selectValue.map((option: any) => option.value)
      },
    })
  }, [fieldName, registerField])

  useEffect(() => {
    setShowError(!!error)
  }, [error])

  useEffect(() => {
    setShowError(!!error)
  }, [error])

  const handleFocus = () => {
    setShowError(false)
  }

  return (
    <InputContainer small={small} large={large} data-testid={dataTestId}>
      <div className="relative">
        <Placeholder
          isFocusedOrValid={selectedOptions.length > 0}
          isDisabled={disabled}
        >
          {placeholder}
          {hasAsterisk && <strong className="asterisk"> *</strong>}
        </Placeholder>
        <Select<ColourOption, true>
          options={options}
          ref={selectRef}
          isMulti
          classNamePrefix="Select"
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          onFocus={handleFocus}
          isSearchable={false}
          styles={selectStyles({
            error: showError,
            small,
            large,
            width,
          })}
          components={{
            Option,
            ValueContainer,
            MultiValue: () => null,
          }}
          onChange={handleChange}
          value={selectedOptions}
          // defaultValue={defaultValue}
          isDisabled={disabled}
          placeholder={''}
        />
      </div>

      <InputError
        error={showError ? error : ''}
        disabled={disabled}
        data-testid={`${dataTestId}-error`}
      >
        <img
          className="img-error"
          src={Icon('alert_circle.svg')}
          alt="error icon"
        />
        {error}
      </InputError>
    </InputContainer>
  )
}

export default MultiSelect
